import { Box, Button, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import type { ColumnDef } from '../common/tableFeatures';
import { StatusChip } from '../common/StatusChip';
import { clipped, dateSortValue, dateTime } from '../common/tableCells';
import type { NonTPMConnectionAttempt } from '@store/api/nonTpmApi';

/** Access expiring within two hours is worth calling out separately. */
const isExpiringSoon = (seconds: number): boolean => seconds > 0 && seconds < 7200;

/** Time left until expiry, coarsened to the largest useful unit. */
const formatTimeRemaining = (seconds: number): string => {
  if (seconds <= 0) return 'Expired';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''}`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

const accessStatus = (attempt: NonTPMConnectionAttempt) => {
  if (attempt.temporary_access_granted && !attempt.is_access_expired) {
    return isExpiringSoon(attempt.time_until_expiry) ? 'expiring_soon' : 'active_access';
  }
  if (attempt.is_access_expired) return 'expired';
  if (attempt.reviewed && attempt.review_decision === 'approved') return 'approved';
  if (attempt.reviewed && attempt.review_decision === 'rejected') return 'rejected';
  return 'needs_review';
};

interface NonTpmColumnHandlers {
  onGrantAccess: (attempt: NonTPMConnectionAttempt) => void;
  onRevokeAccess: (deviceId: string) => void;
}

/** TanStack column definitions for the non-TPM connection-attempt table. */
export const createNonTpmColumns = ({
  onGrantAccess,
  onRevokeAccess,
}: NonTpmColumnHandlers): ColumnDef<NonTPMConnectionAttempt>[] => [
  {
    id: 'device_id',
    accessorKey: 'device_id',
    header: 'Device ID',
    cell: ({ row }) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {row.original.device_id}
        </Typography>
        {row.original.hardware_model && (
          <Typography variant="caption" noWrap component="div" sx={{
            color: 'text.secondary'
          }}>
            {row.original.hardware_model}
          </Typography>
        )}
      </Box>
    ),
    size: 220,
  },
  {
    id: 'mac_address',
    accessorKey: 'mac_address',
    header: 'MAC Address',
    cell: ({ row }) => clipped(row.original.mac_address, 150, { mono: true }),
    size: 160,
  },
  {
    id: 'tpm_error_message',
    accessorFn: (row) => row.tpm_error_message || '',
    header: 'TPM Error',
    cell: ({ row }) => clipped(row.original.tpm_error_message, 200, { error: true }),
    size: 220,
  },
  {
    id: 'attempt_count',
    accessorKey: 'attempt_count',
    header: 'Attempts',
    cell: ({ row }) => row.original.attempt_count,
    size: 90,
  },
  {
    id: 'last_attempt_at',
    accessorFn: (row) => dateSortValue(row.last_attempt_at),
    header: 'Last Attempt',
    cell: ({ row }) => dateTime(row.original.last_attempt_at),
    size: 170,
  },
  {
    id: 'status',
    accessorFn: (row) => accessStatus(row),
    header: 'Status',
    cell: ({ row }) => <StatusChip status={accessStatus(row.original)} type="access" />,
    size: 140,
  },
  {
    id: 'temporary_access_expires_at',
    accessorFn: (row) => dateSortValue(row.temporary_access_expires_at),
    header: 'Access Expires',
    cell: ({ row }) => {
      const attempt = row.original;
      if (!attempt.temporary_access_expires_at) return '-';
      return (
        <Box>
          <Typography variant="body2">{dateTime(attempt.temporary_access_expires_at)}</Typography>
          <Typography variant="caption" sx={{
            color: 'text.secondary'
          }}>
            {formatTimeRemaining(attempt.time_until_expiry)}
          </Typography>
        </Box>
      );
    },
    size: 180,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const attempt = row.original;
      return attempt.temporary_access_granted && !attempt.is_access_expired ? (
        <Button
          size="small"
          color="error"
          startIcon={<CancelIcon />}
          onClick={() => onRevokeAccess(attempt.device_id)}
        >
          Revoke
        </Button>
      ) : (
        <Button
          size="small"
          color="primary"
          startIcon={<CheckCircleIcon />}
          onClick={() => onGrantAccess(attempt)}
        >
          Grant Access
        </Button>
      );
    },
    enableSorting: false,
    size: 150,
    meta: { sticky: 'right' },
  },
];
